import * as React from "react";
import { cn } from "./utils";

interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: "primary" | "secondary" | "tertiary" | "text";
  size?: "sm" | "md" | "lg" | "small";
  fullWidth?: boolean;
  loading?: boolean;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  type?: "button" | "submit" | "reset" | "primary" | "text" | "default"; // Support Ant Design type as well
}

const Button: React.FC<ButtonProps> = ({
  variant: variantProp = "primary",
  size: sizeProp = "md",
  fullWidth = false,
  loading = false,
  className,
  disabled,
  children,
  icon,
  type = "button",
  ...props
}) => {
  // Map Ant Design type values to variant
  const variant = type === "primary" ? "primary" : type === "text" ? "text" : type === "default" ? "secondary" : variantProp;
  // Map Ant Design size values
  const size = sizeProp === "small" ? "sm" : sizeProp;
  // Get actual button type (only valid HTML button types)
  const buttonType = (type === "primary" || type === "text" || type === "default") ? "button" : type;
  const baseClasses = [
    "font-semibold rounded-md transition-all duration-200 cursor-pointer",
    "focus:outline-none focus:ring-2 focus:ring-accent/50 focus:ring-offset-2",
    "disabled:cursor-not-allowed disabled:opacity-50",
  ];

  const variants = {
    primary: [
      "bg-accent text-white",
      "hover:brightness-110",
      "active:brightness-95",
    ],
    secondary: [
      "bg-secondary text-primary border border-secondary",
      "hover:bg-accent hover:text-white hover:border-accent",
      "active:bg-accent/90",
    ],
    tertiary: [
      "bg-transparent text-accent border border-accent/30",
      "hover:bg-accent/10 hover:border-accent",
      "active:bg-accent/20",
    ],
    text: [
      "bg-transparent text-accent border-none",
      "hover:bg-accent/10",
      "active:bg-accent/20",
    ],
  };

  const sizes = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-6 py-2 text-base",
    lg: "px-8 py-3 text-lg",
  };

  const classes = cn(
    baseClasses,
    variants[variant],
    sizes[size],
    fullWidth && "w-full",
    (disabled || loading) && "cursor-not-allowed opacity-50",
    className
  );

  return (
    <button className={classes} disabled={disabled || loading} type={buttonType} {...props}>
      {loading ? (
        <div className="flex items-center justify-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
          <span>Loading...</span>
        </div>
      ) : (
        <span className="flex items-center justify-center gap-2">
          {icon}
          {children}
        </span>
      )}
    </button>
  );
};

export default Button;
